/**
 * SOLID Review
 * S — Strong. One concern: perform one /health probe and classify the outcome.
 * O — Strong. A new outcome is a field on the report; the callers branch on it.
 * L — Strong. Implements the port interface exactly.
 * I — Strong. The port's one op.
 * D — Strong. Takes the resolved URL and the `fetch` it is handed; it reaches into neither import.meta.env nor the window.
 */
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { IEntityHealthReport } from '../../domain/ports/schemas/HealthMoves';
import type { IPortClientServerHealth } from '../../application/ports/contracts';

const UNREACHABLE: IEntityHealthReport = { reachable: false, ok: false, body: null };
/** Every probe asks the server, never the HTTP cache. */
const NO_STORE: RequestInit = { cache: 'no-store' };

/** How the probe reaches the server: the browser's `fetch`, handed in (frontend-infrastructure-oo P2, §28.287). */
export type THealthFetch = (url: string, init: RequestInit) => Promise<Pick<Response, 'ok' | 'status' | 'json'>>;

/**
 * THE HEALTH PROBE (`HealthAdapter`, behind the `ClientServerHealth` port since frontend strict-ports
 * FE5a, 2026-10-04). `fetch` lives HERE, which is the point: it used to live in two framework components,
 * each with its own copy of the polling and failure handling. Infrastructure is where a transport belongs.
 *
 * NOT ROUTED THROUGH a wire socket, and deliberately. The backend's RestHealthApi lollipop is consumed by
 * the Docker Swarm healthcheck — its real counterparty, on the EXTERNAL_COUNTERPARTY allowlist for that
 * reason. A wire socket here would widen IPortRestApiClient to carry a 503 body for one endpoint, a worse
 * trade than one adapter owning one `fetch`. The probe never fails: an unreachable server is a REPORT.
 */
export class ClientServerHealthAdapter implements IPortClientServerHealth {
  constructor(private readonly healthUrl: string, private readonly fetch: THealthFetch) {}

  async probe(_ctx: IContextRequest) {
    return { success: true as const, value: await this.report() };
  }

  private async report(): Promise<IEntityHealthReport> {
    try {
      // `no-store`: a cached 200 would keep the maintenance banner hidden through an outage.
      const res = await this.fetch(this.healthUrl, NO_STORE);
      // 503 IS a body-bearing answer — the server saying it is degraded. Treating it as a failure
      // is what would lose the very payload HealthIndicator renders.
      if (res.ok || res.status === 503) {
        const body: unknown = await res.json().catch(() => null);
        const report: IEntityHealthReport = { reachable: true, ok: res.ok, body: this.objectOf(body) };
        return report;
      }
      const refused: IEntityHealthReport = { reachable: true, ok: false, body: null };
      return refused;
    } catch {
      return UNREACHABLE;
    }
  }

  /** The server's JSON object, or none: anything else is not a body the indicator can read. */
  private objectOf(value: unknown): Record<string, unknown> | null {
    return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
  }
}
